import React, { useState, useEffect } from "react";
import { ShieldOff, CheckCircle2, Info, Smartphone, Bell } from "lucide-react";

const OPTOUT_COOKIE = "monetiseup_optout";
const OPTOUT_TTL_DAYS = 365 * 5; // 5 years

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const domain =
    typeof window !== "undefined" && window.location.hostname.endsWith("monetiseup.com")
      ? "; domain=.monetiseup.com"
      : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/${domain}; SameSite=Lax`;
}

export default function OptOut() {
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    setOptedOut(readCookie(OPTOUT_COOKIE) === "1");
  }, []);

  const doOptOut = () => {
    writeCookie(OPTOUT_COOKIE, "1", OPTOUT_TTL_DAYS);
    setOptedOut(true);
  };

  const undo = () => {
    writeCookie(OPTOUT_COOKIE, "", -1);
    setOptedOut(false);
  };

  return (
    <main className="relative min-h-screen pt-32 pb-20">
      <div className="aurora-orb bg-[#0047FF] w-[600px] h-[600px] -top-40 -left-40 opacity-25" />
      <div className="aurora-orb bg-[#00F0FF] w-[500px] h-[500px] top-1/2 right-0 opacity-15" />

      <div className="container-x relative z-10 max-w-4xl">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
          <ShieldOff className="w-3.5 h-3.5 text-[#00F0FF]" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/80">
            Privacy Choice
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] mb-4">
          Opt out of <span className="aurora-text">interest-based ads</span>
        </h1>
        <p className="text-white/65 leading-relaxed max-w-2xl mb-10">
          MonetiseUP believes advertising can be relevant without being
          intrusive. If you would prefer not to receive ads tailored to your
          browsing patterns, you can opt out below. Your choice is respected
          across every publisher in the MonetiseUP network.
        </p>

        {/* CTA card */}
        <section
          data-testid="optout-card"
          className={`glass-strong rounded-3xl p-8 md:p-10 mb-10 border ${
            optedOut ? "border-emerald-500/30" : "border-white/10"
          }`}
        >
          {!optedOut ? (
            <>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                Turn off tailored advertising
              </h2>
              <p className="text-white/65 mb-6 max-w-2xl">
                Clicking the button will place a small, non-personal cookie on
                this browser. From that moment, MonetiseUP will stop using your
                anonymised browsing signals to select ads on any site in our
                network on this device.
              </p>
              <button
                onClick={doOptOut}
                data-testid="optout-btn"
                className="btn-primary"
              >
                Opt me out
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                <h2 className="font-display text-2xl md:text-3xl font-bold">
                  You're opted out
                </h2>
              </div>
              <p className="text-white/65 mb-6 max-w-2xl">
                We've recorded your preference on this browser. You will
                continue to see general (non-personalised) advertising. To
                reverse this on this browser, click below.
              </p>
              <button
                onClick={undo}
                data-testid="optout-undo"
                className="btn-ghost"
              >
                Undo opt-out
              </button>
            </>
          )}
        </section>

        {/* Sections */}
        <div className="space-y-6">
          <Section
            icon={<Info className="w-5 h-5 text-[#00F0FF]" />}
            title="What happens when you opt out"
          >
            <p className="text-white/65 leading-relaxed">
              A browser cookie is stored with the value{" "}
              <code className="text-[#00F0FF]">{OPTOUT_COOKIE}=1</code>. Our
              serving stack checks for this signal on every request from your
              device and, when present, skips interest-based selection. Ads
              still appear — they just are no longer personalised to your
              inferred preferences.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-white/70">
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                The opt-out is browser-specific. If you switch browsers or
                devices, please repeat the process on each one.
              </li>
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                Clearing your browser cookies will also clear your opt-out
                preference. Come back here and opt out again if that happens.
              </li>
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                The cookie contains no personal data — just a flag telling our
                servers to skip personalisation.
              </li>
            </ul>
          </Section>

          <Section
            icon={<Smartphone className="w-5 h-5 text-[#00F0FF]" />}
            title="Mobile apps"
          >
            <p className="text-white/65 leading-relaxed">
              MonetiseUP does not run interest-based advertising on mobile
              in-app inventory and does not collect device advertising
              identifiers for personalisation purposes. There is no separate
              opt-out required for mobile apps served through our SDK.
            </p>
          </Section>

          <Section
            icon={<Bell className="w-5 h-5 text-[#00F0FF]" />}
            title="Push notifications"
          >
            <p className="text-white/65 leading-relaxed">
              Push notifications are controlled by your browser or operating
              system, not by MonetiseUP directly. To stop notifications from a
              specific site:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-white/70">
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                <span>
                  <b>Chrome / Edge / Brave:</b> Settings → Privacy &amp;
                  Security → Site Settings → Notifications, then remove the
                  site.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                <span>
                  <b>Firefox:</b> Settings → Privacy &amp; Security →
                  Permissions → Notifications → Settings, remove the site.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                <span>
                  <b>Safari (macOS):</b> Safari menu → Settings → Websites →
                  Notifications, set the site to Deny or remove it.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#00F0FF]">•</span>
                <span>
                  <b>iOS / Android:</b> use your device Settings →
                  Notifications, then locate the browser or app and disable
                  notifications.
                </span>
              </li>
            </ul>
          </Section>

          <Section
            icon={<Info className="w-5 h-5 text-[#00F0FF]" />}
            title="GDPR, CCPA and regional rights"
          >
            <p className="text-white/65 leading-relaxed">
              Residents of the EU, UK, California and other jurisdictions with
              relevant privacy laws have additional rights, including access,
              correction and deletion of personal data. See our{" "}
              <a
                href="/privacy-policy"
                className="text-[#00F0FF] hover:text-white transition-colors"
              >
                Privacy Policy
              </a>{" "}
              for full details, or email{" "}
              <a
                href="mailto:business@responsiq.com"
                className="text-[#00F0FF] hover:text-white transition-colors"
              >
                business@responsiq.com
              </a>{" "}
              to exercise your rights.
            </p>
          </Section>
        </div>
      </div>
    </main>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="glass rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-4">
        {icon}
        <h3 className="font-display text-xl md:text-2xl font-bold">{title}</h3>
      </div>
      {children}
    </div>
  );
}
